import { useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type { GetInboxTokenParams, GetInboxTokenResponse, ProblemDetails } from '../models';

import { useCustomNotificationsClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Get a token for the inbox<br><br><b>Required Scopes</b><br>notifications.doka.com/notifications.inbox
 */
export const useGetInboxTokenHook = () => {
    const getInboxToken = useCustomNotificationsClient<GetInboxTokenResponse>();

    return useCallback(
        (params?: GetInboxTokenParams, signal?: AbortSignal) => {
            return getInboxToken({ url: `/inbox/token`, method: 'GET', params, signal });
        },
        [getInboxToken],
    );
};

export const getGetInboxTokenQueryKey = (params?: GetInboxTokenParams) => {
    return [`/inbox/token`, ...(params ? [params] : [])] as const;
};

export const useGetInboxTokenQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetInboxTokenParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetInboxTokenQueryKey(params);

    const getInboxToken = useGetInboxTokenHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>> = ({ signal }) =>
        getInboxToken(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInboxTokenQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>>;
export type GetInboxTokenQueryError = ErrorType<ProblemDetails>;

export function useGetInboxToken<
    TData = Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetInboxTokenParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetInboxToken<
    TData = Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetInboxTokenParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetInboxToken<
    TData = Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetInboxTokenParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetInboxToken<
    TData = Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetInboxTokenParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInboxTokenHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetInboxTokenQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}
